'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronRight, Calculator } from 'lucide-react';
import { BarbellIcon } from '@/components/icons/BarbellIcon';
import { TacticalChartIcon } from '@/components/icons/TacticalChartIcon';
import { CrosshairIcon } from '@/components/icons/CrosshairIcon';
import { Clock } from 'lucide-react';
import WeeklyMissionsCard from '@/components/WeeklyMissionsCard';
import { LAUNCH_FLAGS } from '@/lib/launchFlags';
import GunfighterWidget, { GunfighterCrossSellBanner } from '@/components/GunfighterWidget';
import { isGunfighterEnabled } from '@/lib/platformFlags';
import SpearheadDashboardCard from '@/components/SpearheadDashboardCard';
import WeekScheduleCard from '@/components/dashboard/WeekScheduleCard';
import CollapsibleSection from '@/components/dashboard/CollapsibleSection';
import { useUiPreferences } from '@/lib/hooks/useUiPreferences';
import { isAreaEnabled } from '@/lib/uiPreferences';
import type { DashboardData } from '@/lib/hooks/useDashboardData';
import type { SpearheadProgress, SpearheadDomain } from '@/lib/types/spearhead';

interface SharedDashboardTailProps {
  data: DashboardData;
}

export default function SharedDashboardTail({ data }: SharedDashboardTailProps) {
  const {
    user,
    currentWeek,
    currentDay,
    programSelection,
    intelligenceReport,
    activeOp,
    todayActivity,
    dailyCalories,
    workoutTab,
    setWorkoutTab,
    shouldShowAIBrief,
  } = data;

  const { prefs } = useUiPreferences();
  const showOps = isAreaEnabled(prefs, 'operations');

  // Load Spearhead progress for territory operations overlay
  const [spearheadProgress, setSpearheadProgress] = useState<SpearheadProgress | null>(null);
  useEffect(() => {
    if (!user?.uid || activeOp?.operationType !== 'territory') return;
    import('@/lib/spearheadEngine').then(({ loadSpearheadProgress, isSpearheadActive }) => {
      if (!isSpearheadActive()) return;
      loadSpearheadProgress(user.uid).then(setSpearheadProgress).catch(() => {});
    });
  }, [user?.uid, activeOp]);

  // Days-remaining is time-dependent — must be computed on the client AFTER
  // mount, not in the render body, or static-export build time and client
  // mount time will produce different counts and trigger React #300.
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  useEffect(() => {
    if (!activeOp?.endDate) {
      setDaysRemaining(null);
      return;
    }
    const end = new Date(activeOp.endDate).getTime();
    setDaysRemaining(Math.max(0, Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24))));
  }, [activeOp?.endDate]);

  const DOMAIN_COLORS: Record<SpearheadDomain, string> = {
    alpha: 'bg-red-500',
    bravo: 'bg-blue-500',
    charlie: 'bg-emerald-500',
    delta: 'bg-amber-500',
  };

  return (
    <>
      {/* SPEARHEAD — Territory control quick-view (hide when active op IS Spearhead to avoid duplication) */}
      {showOps && user?.uid && !(activeOp?.operationType === 'territory') && <SpearheadDashboardCard userId={user.uid} />}

      {/* CORRUPTED OPERATION CARD — ARG breadcrumb when no active op */}
      {showOps && !activeOp && <CorruptedOperationCard />}

      {/* ACTIVE OPERATION — Hero card with marketing image */}
      {showOps && activeOp && (
        <Link href="/operations" className="block group">
          <div className="relative rounded-xl overflow-hidden border-2 border-blue-500/30 hover:border-blue-500/60 transition-all">
            <div className="relative h-40 sm:h-48">
              {activeOp.theme?.heroImage ? (
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${activeOp.theme.heroImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-slate-900" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-blue-300 font-semibold mb-1">
                  {activeOp.operationType === 'territory' ? 'TERRITORY OPERATION' : 'SEASONAL OPERATION'}
                </div>
                <div className="text-xl sm:text-2xl font-extrabold text-white group-hover:text-blue-300 transition leading-tight">
                  {activeOp.name}
                </div>
                <div className="text-xs text-gray-300 mt-1 line-clamp-2 max-w-lg">
                  {activeOp.story?.situation || activeOp.description}
                </div>
                {/* Territory sector bars (when Spearhead) */}
                {activeOp.operationType === 'territory' && spearheadProgress && (
                  <div className="grid grid-cols-4 gap-2 mt-3 mb-2">
                    {(['alpha', 'bravo', 'charlie', 'delta'] as SpearheadDomain[]).map(d => {
                      const pct = Math.round(spearheadProgress.sectors[d].controlPct);
                      return (
                        <div key={d}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] text-gray-400 font-bold uppercase">{d[0]}</span>
                            <span className="text-[10px] text-white font-bold">{pct}%</span>
                          </div>
                          <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${DOMAIN_COLORS[d]} transition-all`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mt-2 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-bold uppercase tracking-wider group-hover:bg-blue-400 transition">
                    {/* Plain English. "Enter AO" is unexplained jargon in a
                        PRIMARY button — the one place the rule matters most. */}
                    Open Operation <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-[11px] text-gray-400">
                    {daysRemaining !== null ? `${daysRemaining} days remaining` : ''}
                  </span>
                  {activeOp.operationType === 'territory' && spearheadProgress && (
                    <span className="text-[10px] text-gray-500">
                      Consistency: <span className="text-white font-semibold">{Math.round(spearheadProgress.consistencyIndex * 100)}%</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* WEEK SCHEDULE — Full week overview (program mode only) */}
      {programSelection && (
        <CollapsibleSection
          sectionKey="week-schedule"
          title={`Week ${currentWeek} Schedule`}
          icon={<BarbellIcon className="w-5 h-5 text-operator-gold" />}
        >
          <WeekScheduleCard
            currentWeek={currentWeek}
            currentDay={currentDay}
            programSelection={programSelection}
          />
        </CollapsibleSection>
      )}

      {/* Intelligence Brief removed — surface real signal directly via the
          reentry banner, weekly missions, and the metrics page rather than
          a catch-all "brief" section. */}

      {/* Weekly Missions — Collapsible (bare: WeeklyMissionsCard has its own panel) */}
      {LAUNCH_FLAGS.weeklyMissions && intelligenceReport && (
        <CollapsibleSection
          sectionKey="weekly-missions"
          title="Weekly Missions"
          bare
        >
          <WeeklyMissionsCard intelligence={intelligenceReport} />
        </CollapsibleSection>
      )}

      {/* Quick Actions — Collapsible */}
      <CollapsibleSection
        sectionKey="quick-actions"
        title="Quick Actions"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { href: '/history', icon: <Clock className="w-6 h-6" />, label: 'History', accent: 'hud-cyan' },
            { href: '/templates', icon: <BarbellIcon className="w-6 h-6" />, label: 'Templates', accent: 'amber' },
            { href: '/workouts/custom', icon: <TacticalChartIcon className="w-6 h-6" />, label: 'Custom', accent: 'slate' },
            { href: '/1rm', icon: <Calculator className="w-6 h-6" />, label: '1RM Calc', accent: 'slate' },
          ].map((a) => {
            const borderTw =
              a.accent === 'hud-cyan' ? 'border-hud-cyan/40 hover:border-hud-cyan/80' :
              a.accent === 'amber' ? 'border-amber-500/40 hover:border-amber-500/80' :
              'border-slate-700 hover:border-slate-500';
            const iconTw =
              a.accent === 'hud-cyan' ? 'text-hud-cyan' :
              a.accent === 'amber' ? 'text-amber-400' :
              'text-slate-300';
            const labelTw =
              a.accent === 'hud-cyan' ? 'text-hud-cyan' :
              a.accent === 'amber' ? 'text-amber-400' :
              'text-slate-400';
            return (
              <Link
                key={a.href}
                href={a.href}
                className={`relative flex flex-col items-center justify-center gap-1.5 px-2 py-3 rounded-md border bg-slate-950/60 transition hover:bg-slate-900/50 ${borderTw}`}
              >
                <span className={iconTw} aria-hidden>{a.icon}</span>
                <div className={`font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${labelTw}`}>
                  {a.label}
                </div>
              </Link>
            );
          })}
        </div>
      </CollapsibleSection>

      {/* GunfighterOS Integration — iOS-hidden (App Store 3.1.1) */}
      {isGunfighterEnabled() && (
        <>
          <GunfighterWidget />
          <GunfighterCrossSellBanner />
        </>
      )}
    </>
  );
}

// ============================================================================
// CORRUPTED OPERATION CARD — ARG breadcrumb shown when no operation is active
// Visually styled like a glitched/intercepted transmission. Links to the
// terminal intercept screen at /operations.
// ============================================================================

function CorruptedOperationCard() {
  return (
    <Link href="/operations" className="block group">
      <div className="relative rounded-xl overflow-hidden border-2 border-emerald-500/30 hover:border-emerald-500/60 transition-all bg-black">
        {/* Scanline overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,170,0.5) 2px, rgba(0,255,170,0.5) 3px)',
          }}
        />

        {/* Vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.8) 100%)',
          }}
        />

        <div className="relative h-40 sm:h-48 p-4 flex flex-col justify-between font-mono">
          {/* Top: Status header */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                <span className="text-[9px] text-amber-400 uppercase tracking-[0.2em] font-bold">
                  SIGNAL LOST
                </span>
              </div>
              <span className="text-[9px] text-emerald-500/40">SIGINT-RELAY-7</span>
            </div>

            {/* Glitched operation name */}
            <div className="text-lg sm:text-xl font-extrabold text-emerald-400 tracking-wider leading-tight">
              <span className="inline-block">OPERATION</span>{' '}
              <span className="inline-block relative">
                <span className="text-red-400">█</span>
                <span className="text-emerald-400/60">PE</span>
                <span className="text-red-400">█</span>
                <span className="text-emerald-400/60">RH</span>
                <span className="text-red-400">█</span>
                <span className="text-emerald-400/60">AD</span>
              </span>
            </div>
            <div className="text-[10px] text-emerald-500/60 mt-1 uppercase tracking-wider">
              [classification redacted]
            </div>
          </div>

          {/* Middle: Compromised message */}
          <div className="text-[10px] text-amber-400/80 leading-snug">
            {'>'} EREBUS network has severed internal channel. Sector grid ECHO-7 unreachable from this terminal.
          </div>

          {/* Bottom: CTA */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 text-[10px] font-bold uppercase tracking-wider group-hover:bg-emerald-500/20 transition">
              {'>'}_ Establish Uplink
            </span>
            <span className="text-[9px] text-emerald-500/50">
              external terminal required
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
